import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Package, Plus, Search, Pencil, Trash2, Download, X, FileSpreadsheet, Loader2 } from 'lucide-react';
import { ExportMenu } from '../components/ExportMenu';


const SATUAN_LIST = [
  'pcs','unit','box','pack','set','lusin','kg','gram',
  'liter','ml','meter','cm','roll','zak','ekor',
  'karton','botol','kaleng','lembar','buah'
];

const EMPTY_FORM = { nama_barang: '', satuan: 'pcs', kode_barang: '' };

export default function Products() {
  const [products, setProducts]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [pages, setPages]         = useState(1);
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(false);

  // Modal tambah/edit
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);   // null = tambah baru
  const [formData, setFormData]   = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);

  // Import
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting]   = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  // ── Fetch produk ──────────────────────────────────────────────────────────
  const fetchProducts = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await api.get(`/products`, {
        params: { page: p, limit: 20, search: search || undefined }
      });
      setProducts(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(p);
    } catch (err) {
      toast.error('Gagal memuat data barang');
    } finally {
      setLoading(false);
    }
  }, [search]);

  // Muat hal 1 saat mount dan setiap search berubah
  useEffect(() => { fetchProducts(1); }, [fetchProducts]);

  // ── Modal tambah/edit ──────────────────────────────────────────────────────
  const openAdd = () => {
    setEditTarget(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (product) => {
    setEditTarget(product);
    setFormData({ nama_barang: product.nama_barang, satuan: product.satuan, kode_barang: product.kode_barang });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.nama_barang.trim()) { toast.error('Nama barang wajib diisi'); return; }
    if (!formData.satuan)             { toast.error('Satuan wajib dipilih');    return; }

    setSaving(true);
    try {
      if (editTarget) {
        await api.put(`/products/${editTarget.id}`, {
          nama_barang: formData.nama_barang,
          satuan: formData.satuan,
        });
        toast.success('Barang berhasil diperbarui');
      } else {
        await api.post(`/products`, formData);
        toast.success('Barang berhasil ditambahkan');
      }
      setShowModal(false);
      fetchProducts(editTarget ? page : 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan barang');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/products/${deleteTarget.id}`);
      toast.success(`Barang '${deleteTarget.nama_barang}' berhasil dihapus`);
      setDeleteTarget(null);
      fetchProducts(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menghapus barang');
    } finally {
      setDeleting(false);
    }
  };

  // ── Download template ──────────────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get(`/products/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href = url; a.download = 'template_daftar_barang.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Gagal mengunduh template');
    }
  };

  // ── Import Excel ───────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!importFile) { toast.error('Pilih file terlebih dahulu'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await api.post(`/products/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      toast.success(res.data.message);
      fetchProducts(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengimpor barang');
    } finally {
      setImporting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Package className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Daftar Barang</h1>
              <p className="text-sm text-slate-500">{total} barang terdaftar</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <ExportMenu table="products" params={{ search }} />
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setShowImport(true); setImportResult(null); setImportFile(null); }} data-testid="import-btn">
              <Download className="h-4 w-4 mr-1" /> Import Excel
            </Button>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={openAdd} data-testid="add-product-btn">
              <Plus className="h-4 w-4 mr-1" /> Tambah Barang
            </Button>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Cari kode atau nama barang..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-products"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Tabel */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-semibold text-slate-600 w-36">Kode Barang</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Nama Barang</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 w-24">Satuan</th>
                <th className="text-center px-4 py-3 font-semibold text-slate-600 w-28">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Memuat data...
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-16 text-center text-slate-400">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>Belum ada barang terdaftar.</p>
                    <p className="text-xs mt-1">Klik "Tambah Barang" atau "Import Excel" untuk mulai.</p>
                  </td>
                </tr>
              ) : (
                products.map((p) => (
                  <tr key={p.id} className="transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded font-semibold">
                        {p.kode_barang}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">{p.nama_barang}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                        {p.satuan}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEdit(p)}
                          className="p-1.5 rounded hover:bg-indigo-100 text-indigo-600 transition-colors"
                          title="Edit"
                          data-testid={`edit-product-${p.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(p)}
                          className="p-1.5 rounded hover:bg-red-100 text-red-500 transition-colors"
                          title="Hapus"
                          data-testid={`delete-product-${p.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50 text-sm text-slate-500">
              <span>Halaman {page} dari {pages} · {total} barang</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1}    onClick={() => fetchProducts(page - 1)}>Sebelumnya</Button>
                <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => fetchProducts(page + 1)}>Berikutnya</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Modal Tambah / Edit ─────────────────────────────────────────────── */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Edit Barang' : 'Tambah Barang Baru'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {!editTarget && (
              <div className="space-y-1">
                <Label className="text-xs">Kode Barang <span className="text-slate-400">(kosongkan = otomatis)</span></Label>
                <Input
                  value={formData.kode_barang}
                  onChange={(e) => setFormData(f => ({ ...f, kode_barang: e.target.value.toUpperCase() }))}
                  placeholder="BRG-00001"
                  className="font-mono"
                  data-testid="input-kode-barang"
                />
              </div>
            )}
            {editTarget && (
              <div className="space-y-1">
                <Label className="text-xs">Kode Barang</Label>
                <Input value={formData.kode_barang} disabled className="font-mono bg-slate-50" />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Nama Barang *</Label>
              <Input
                value={formData.nama_barang}
                onChange={(e) => setFormData(f => ({ ...f, nama_barang: e.target.value.toUpperCase() }))}
                placeholder="NAMA BARANG"
                style={{ textTransform: 'uppercase' }}
                data-testid="input-nama-barang"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Satuan *</Label>
              <Select value={formData.satuan} onValueChange={(v) => setFormData(f => ({ ...f, satuan: v }))}>
                <SelectTrigger data-testid="select-satuan">
                  <SelectValue placeholder="Pilih satuan" />
                </SelectTrigger>
                <SelectContent>
                  {SATUAN_LIST.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSave} disabled={saving} data-testid="save-product-btn">
                {saving ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Modal Import Excel ──────────────────────────────────────────────── */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-indigo-600" /> Import Daftar Barang dari Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-800">
              <p className="font-semibold mb-1">Panduan:</p>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>Gunakan template resmi (klik tombol "Template" di halaman utama)</li>
                <li>Kolom <strong>Nama Barang</strong> dan <strong>Satuan</strong> wajib diisi</li>
                <li>Kode Barang bisa dikosongkan — akan dibuat otomatis</li>
                <li>Barang yang sudah terdaftar (nama sama) akan dilewati</li>
              </ul>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Pilih File Excel (.xlsx)</Label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => { setImportFile(e.target.files[0]); setImportResult(null); }}
                className="block w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
                data-testid="import-file-input"
              />
              {importFile && (
                <p className="text-xs text-slate-500">File: <strong>{importFile.name}</strong> ({(importFile.size / 1024).toFixed(1)} KB)</p>
              )}
            </div>

            {/* Hasil import */}
            {importResult && (
              <div className={`rounded-lg p-3 text-sm ${importResult.imported > 0 ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
                <p className="font-semibold">{importResult.message}</p>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold text-red-700 mb-1">Error ({importResult.errors.length}):</p>
                    <ul className="list-disc list-inside text-xs text-red-600 space-y-0.5 max-h-28 overflow-y-auto">
                      {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowImport(false)}>Tutup</Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleImport}
                disabled={!importFile || importing}
                data-testid="confirm-import-btn"
              >
                {importing ? 'Mengimpor...' : 'Import Sekarang'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Konfirmasi Hapus ────────────────────────────────────────────────── */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">Hapus Barang?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mt-2">
            Barang <strong>{deleteTarget?.nama_barang}</strong> ({deleteTarget?.kode_barang}) akan dihapus dari katalog.
          </p>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Batal</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
              {deleting ? 'Menghapus...' : 'Ya, Hapus'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}



